import React, { Fragment, useEffect, useState } from 'react';

import { CircularProgress } from '@mui/material';

import { Button } from '@/components/NewUI/ComponentsLibrary';

import type { ButtonProps } from '@mui/material';
import type { FC, PropsWithChildren } from 'react';

interface Props extends PropsWithChildren<ButtonProps> {
  buttonVariant?: 'primary' | 'secondary' | 'text' | 'outline';
  loadingCaption?: string;
  task: () => Promise<unknown>;
}

interface ViewProps extends Props {
  isLoading: boolean;
}

export const LoadingButtonView: FC<ViewProps> = (props) => {
  const { children, disabled, isLoading, loadingCaption, sx, task, variant, ...rest } = props;

  return (
    <Button
      {...rest}
      disabled={isLoading || disabled}
      sx={{ display: 'flex', alignItems: 'center', ...sx }}
      variant={variant ?? 'contained'}
      onClick={
        !isLoading
          ? async (e) => {
              e.preventDefault();
              return task();
            }
          : undefined
      }
    >
      {isLoading ? (
        <>
          <CircularProgress color="inherit" role="status" size={20} />
          <span>{loadingCaption}</span>
        </>
      ) : (
        children
      )}
    </Button>
  );
};

export const LoadingButton: FC<Props> = (props) => {
  const { children, task, ...rest } = props;
  const [isLoading, setLoading] = useState(false);

  const wrappedTask = async (): Promise<unknown> => {
    setLoading(true);
    return task()
      .catch()
      .finally(() => setLoading(false));
  };

  useEffect(
    () => () => {
      setLoading(false);
    },
    []
  );

  return (
    <LoadingButtonView {...rest} isLoading={isLoading} task={wrappedTask}>
      {children}
    </LoadingButtonView>
  );
};

export default LoadingButton;
